/**
 * Fallback calculation utilities for TimechainStats
 *
 * Provides fallback calculations for metrics when API data is unavailable.
 * Data source priority (WebSocket-first for speed):
 * 1. Mempool.space WebSocket - Real-time recent blocks (FAST)
 * 2. Diamondback API (2140data.com) - 30 days of accurate block data (FALLBACK)
 * 3. s091 API - Historical data (often stale/broken)
 * 4. Theoretical defaults - Fallback values
 */

const DIAMONDBACK_API = 'https://2140data.com/api';

/**
 * Fetch network statistics from Diamondback API
 * @param {number} period - Number of blocks (144, 2016, or 4032)
 * @returns {Promise<Object|null>} Network stats or null on error
 */
export const fetchDiamondbackStats = async (period = 144) => {
  try {
    const response = await fetch(`${DIAMONDBACK_API}/stats/network?period=${period}`);
    if (!response.ok) return null;
    const data = await response.json();
    return data.stats;
  } catch (error) {
    console.warn('[Diamondback] Failed to fetch stats:', error.message);
    return null;
  }
};

/**
 * Fetch recent blocks from Diamondback API
 * @param {number} count - Number of blocks to fetch (default: 144)
 * @returns {Promise<Array|null>} Array of blocks or null on error
 */
export const fetchDiamondbackBlocks = async (count = 144) => {
  try {
    const response = await fetch(`${DIAMONDBACK_API}/blocks/${count}`);
    if (!response.ok) return null;
    const blocks = await response.json();
    return blocks;
  } catch (error) {
    console.warn('[Diamondback] Failed to fetch blocks:', error.message);
    return null;
  }
};

/**
 * Calculate average block time from recent blocks.
 *
 * Spans total elapsed time over the height delta rather than averaging per-row
 * intervals. If the source is missing blocks (e.g. Diamondback after an outage)
 * the rows held are a subset of what was actually mined, so a per-row average
 * counts each gap as one long interval and inflates the result.
 * The height delta is the true interval count regardless of which rows survived.
 *
 * @param {Array} recentBlocks - Array from recentBlocksState, ascending by height
 * @returns {number|null} Average block time in seconds
 */
export const calculateAvgBlockTime = (recentBlocks) => {
  if (!recentBlocks || recentBlocks.length < 2) return null;

  const oldest = recentBlocks[0];
  const newest = recentBlocks[recentBlocks.length - 1];

  const timeSpan = newest.timestamp - oldest.timestamp;
  const intervals = newest.height - oldest.height;

  if (!(timeSpan > 0) || !(intervals > 0)) return null;

  return timeSpan / intervals;
};

/**
 * Reference block time from the mempool difficulty-adjustment payload.
 *
 * mempool reports timeAvg in milliseconds, computed by its own indexer across the
 * current retarget epoch. It is derived independently of any block rows we hold,
 * so gaps in our block data cannot skew it — which makes it a valid cross-check.
 *
 * @param {Object} da - Difficulty adjustment payload (mempool WebSocket or REST)
 * @returns {number|null} Reference average block time in seconds
 */
export const getReferenceBlockTime = (da) => {
  const raw = da?.adjustedTimeAvg ?? da?.timeAvg;
  if (!raw || raw <= 0) return null;
  return raw / 1000;
};

// A block-derived average beyond this factor either side of the epoch reference
// indicates the underlying block set is unusable, not that the network changed.
const BLOCK_TIME_TOLERANCE = 2;

/**
 * Validate a block-derived average block time against the epoch reference,
 * falling back to the reference when the candidate is implausible.
 *
 * @param {number|null} candidate - Block-derived average in seconds
 * @param {Object} da - Difficulty adjustment payload
 * @returns {number|null} Trustworthy average block time in seconds
 */
export const resolveAvgBlockTime = (candidate, da) => {
  const reference = getReferenceBlockTime(da);

  if (candidate == null || isNaN(candidate) || candidate <= 0) return reference;
  if (reference == null) return candidate;

  const ratio = candidate / reference;
  const trusted = ratio <= BLOCK_TIME_TOLERANCE && ratio >= 1 / BLOCK_TIME_TOLERANCE;

  return trusted ? candidate : reference;
};

/**
 * Calculate blocks mined in last 24 hours.
 * Returns null unless the array actually spans ~24 hours, otherwise the count
 * is meaningless (e.g. 8 blocks accumulated since page load is not "8 blocks/24h").
 * @param {Array} recentBlocks - Array from recentBlocksState
 * @returns {number|null} Number of blocks in last 24 hours, or null if history is too short
 */
export const calculateNewBlocks24h = (recentBlocks) => {
  if (!recentBlocks || recentBlocks.length < 2) return null;

  const now = Date.now() / 1000;
  const blocks24h = recentBlocks.filter(
    block => (now - block.timestamp) <= 86400
  );

  if (blocks24h.length === 0) return null;

  // Require oldest block to be at least 22h old before trusting the count.
  // Anything shorter is an undercount of the real 24h window.
  const oldest = blocks24h.reduce((min, b) => b.timestamp < min ? b.timestamp : min, blocks24h[0].timestamp);
  if ((now - oldest) < 79200) return null;

  return blocks24h.length;
};

/**
 * Fallback priority chain for metrics
 * Tries multiple data sources in priority order, returning the first valid value
 *
 * Data Source Priority (WebSocket-first for speed):
 * 1. Mempool WebSocket - Calculate from recent blocks in memory (FAST)
 * 2. Diamondback API (2140data.com) - Pre-calculated stats (FALLBACK)
 * 3. s091 API - Historical data (often stale)
 * 4. Theoretical defaults - Last resort
 *
 * @param {string} metricName - Name of the metric to get
 * @param {Object} sources - Object containing all available data sources
 * @param {Object} sources.diamondbackStats - Pre-fetched stats from Diamondback API
 * @param {Array} sources.recentBlocks - Recent blocks from mempool WebSocket
 * @param {Object} sources.s091 - Data from s091 API endpoint
 * @returns {number|null} Calculated or fallback value, or null if all fail
 */
export const getFallbackValue = (metricName, sources) => {
  const strategies = {
    avgBlockTime: [
      // Priority 1: Calculate from mempool WebSocket blocks (FAST - already in memory)
      () => calculateAvgBlockTime(sources.recentBlocks),
      // Priority 2: Diamondback API (pre-calculated from 144 blocks)
      () => sources.diamondbackStats?.avgBlockTime,
      // Priority 3: Try s091 API data
      () => sources.s091?.btc_historical?.Data?.Data?.[1]?.block_time,
      // Priority 4: Default to theoretical 10 minutes
      () => 600
    ],
    newBlocks24h: [
      // Priority 1: Calculate from mempool WebSocket blocks (FAST)
      () => calculateNewBlocks24h(sources.recentBlocks),
      // Priority 2: Diamondback API (actual count from last 144 blocks)
      () => {
        if (sources.diamondbackBlocks && Array.isArray(sources.diamondbackBlocks)) {
          return sources.diamondbackBlocks.length;
        }
        return null;
      },
      // Priority 3: Try s091 API data
      () => {
        const data = sources.s091?.btc_historical?.Data?.Data;
        if (!data || data.length < 2) return null;
        return data[1]?.block_height - data[0]?.block_height;
      },
      // No theoretical default — let the consumer show a loading state instead of
      // briefly displaying 144 (the theoretical 6×24 rate) as if it were real data.
    ],
  };

  const chain = strategies[metricName] || [];

  // Try each strategy in priority order
  for (const strategy of chain) {
    try {
      const value = strategy();
      // Return first valid, non-null, non-NaN value
      if (value != null && !isNaN(value)) {
        return value;
      }
    } catch (error) {
      // Silently continue to next strategy on error
      continue;
    }
  }

  // All strategies failed
  return null;
};
